import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import { PageShell } from "@/components/layout/PageShell";
import { SectionHeader } from "@/components/common/SectionHeader";
import { AdSlot } from "@/components/common/AdSlot";
import { StatTile } from "@/components/common/StatTile";
import { DistrictCard } from "@/components/common/DistrictCard";
import { OfficeBearerCard } from "@/components/common/OfficeBearerCard";
import { GRListItem } from "@/components/common/GRListItem";
import { GaplCountdown } from "@/components/common/GaplCountdown";
import { HeroSlideshow } from "@/components/common/HeroSlideshow";
import { SocialRow } from "@/components/common/SocialRow";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, X, LogIn, MessageSquareWarning, ArrowRight, Eye, Users, CalendarDays } from "lucide-react";
import { GMOAA } from "@/constants/testIds";

const Home = () => {
  const { t, pick, tf, lang, site } = useLang();
  const navigate = useNavigate();
  const [notice, setNotice] = useState(null);
  const [noticeOpen, setNoticeOpen] = useState(true);
  const [stats, setStats] = useState([]);
  const [counters, setCounters] = useState(null);
  const [bearers, setBearers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [grs, setGrs] = useState([]);
  const [notices, setNotices] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [gapl, setGapl] = useState(null);
  const [ads, setAds] = useState({});
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    api.importantNotice().then((d) => setNotice(d.value));
    api.stats().then(setStats);
    api.counters().then(setCounters);
    api.officeBearers().then((d) => setBearers(d.slice(0, 4)));
    api.districts().then((d) => setDistricts(d));
    api.grs({ type: "gr" }).then((d) => setGrs(d.slice(0, 4)));
    api.grs({ type: "notice" }).then((d) => setNotices(d.slice(0, 4)));
    api.upcoming().then(setUpcoming);
    api.gapl().then(setGapl);
    api.sponsors().then(setSponsors);
    api.ads().then((all) => {
      const map = {};
      all.forEach((a) => { if (!map[a.slot]) map[a.slot] = a; });
      setAds(map);
    });
  }, []);

  const home = site?.home || {};
  const hero = home.hero || {};
  const gaplCfg = home.gapl || {};
  const countersOn = counters && counters.enabled !== false;
  const sections = (home.sections && home.sections.length
    ? [...home.sections].sort((a, b) => (a.order || 0) - (b.order || 0))
    : [
        { key: "important_bar", show: true }, { key: "hero", show: true }, { key: "stats", show: true },
        { key: "counters", show: true }, { key: "gr_notices", show: true }, { key: "bearers", show: true },
        { key: "districts", show: true }, { key: "sponsors", show: true },
      ]);
  const visible = (k) => sections.some((s) => s.key === k && s.show !== false);

  const L = (en, gu) => (lang === "gu" ? (gu || en) : en);

  const blocks = {
    important_bar: notice && noticeOpen ? (
      <div key="important_bar" data-testid={GMOAA.importantBar} className="bg-gmoaa-red text-white">
        <div className="gmoaa-container flex items-center gap-3 py-2 text-sm">
          <span className="flex shrink-0 items-center gap-1 rounded bg-white/20 px-2 py-0.5 text-xs font-bold uppercase">
            <AlertTriangle size={13} /> {t("importantLabel")}
          </span>
          <div className="flex-1 overflow-hidden"><p className="whitespace-nowrap">{pick(notice)}</p></div>
          <button data-testid={GMOAA.importantDismiss} onClick={() => setNoticeOpen(false)} className="shrink-0 rounded p-1 hover:bg-white/20"><X size={16} /></button>
        </div>
      </div>
    ) : null,

    hero: (
      <React.Fragment key="hero">
        <div className="gmoaa-container pt-6">{ads.top && <AdSlot ad={ads.top} height="h-24" />}</div>
        <section className="gmoaa-container grid grid-cols-1 gap-8 py-10 lg:grid-cols-2 lg:items-center">
          <div className="animate-fade-in">
            {hero.show_badge !== false && (
              <span className="inline-block rounded-full bg-gmoaa-saffron-light px-3 py-1 text-xs font-semibold text-gmoaa-saffron-hover" data-testid="hero-badge">
                {L(hero.badge_en, hero.badge_gu) || "Est. 1999 · Gandhinagar"}
              </span>
            )}
            <h1 className="mt-4 font-serif text-3xl font-bold leading-tight text-gmoaa-green sm:text-4xl lg:text-5xl">
              {L(hero.title_en, hero.title_gu) || t("heroTitle")}
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-gmoaa-text-muted sm:text-base">{L(hero.subtitle_en, hero.subtitle_gu) || t("heroSubtitle")}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {hero.primary_show !== false && (
                <Button onClick={() => navigate("/member/login")} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">
                  <LogIn size={16} className="mr-1.5" /> {L(hero.primary_en, hero.primary_gu) || t("memberLogin")}
                </Button>
              )}
              {hero.secondary_show !== false && (
                <Button variant="outline" onClick={() => navigate("/contact")} className="border-gmoaa-saffron text-gmoaa-saffron-hover hover:bg-gmoaa-saffron-light">
                  <MessageSquareWarning size={16} className="mr-1.5" /> {L(hero.secondary_en, hero.secondary_gu) || t("raiseIssue")}
                </Button>
              )}
            </div>
          </div>
          <div className="relative animate-fade-in">
            <HeroSlideshow />
            {gapl && gaplCfg.show_countdown !== false && (
              <div className="mt-4">
                <GaplCountdown startDate={gapl.start_date} title={L(gaplCfg.countdown_title_en, gaplCfg.countdown_title_gu)} />
                <Link to="/gapl" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-gmoaa-green hover:text-gmoaa-saffron-hover">
                  {t("viewGapl")} <ArrowRight size={15} />
                </Link>
              </div>
            )}
          </div>
        </section>
      </React.Fragment>
    ),

    stats: (
      <section key="stats" className="gmoaa-container py-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => <StatTile key={s.id} stat={s} />)}
        </div>
      </section>
    ),

    counters: countersOn ? (
      <section key="counters" className="bg-gmoaa-green py-4 text-white">
        <div className="gmoaa-container flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm">
          <span className="flex items-center gap-2"><span className="h-2 w-2 animate-pulse rounded-full bg-gmoaa-gold" /> {t("liveCounters")}</span>
          <span className="flex items-center gap-2"><Eye size={16} className="text-gmoaa-gold" /> {t("visitorsToday")}: <b className="tabular-nums">{counters.visitors_today}</b></span>
          <span className="flex items-center gap-2"><Users size={16} className="text-gmoaa-gold" /> {t("visitorsTotal")}: <b className="tabular-nums">{Number(counters.visitors_total).toLocaleString()}</b></span>
          <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-400" /> {t("membersOnline")}: <b className="tabular-nums">{counters.members_online}</b></span>
        </div>
        <div className="gmoaa-container mx-auto my-3 h-px max-w-3xl bg-gmoaa-gold/40" />
        <div className="gmoaa-container"><SocialRow /></div>
      </section>
    ) : null,

    gr_notices: (
      <section key="gr_notices" className="gmoaa-container py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHeader titleKey="grTitle" action={
              <Link to="/gr-notices" className="inline-flex items-center gap-1 text-sm font-medium text-gmoaa-green hover:text-gmoaa-saffron-hover">{t("viewAll")} <ArrowRight size={15} /></Link>
            } />
            <Tabs defaultValue="gr">
              <TabsList className="bg-gmoaa-green-light">
                <TabsTrigger data-testid={GMOAA.grTabGr} value="gr" className="data-[state=active]:bg-gmoaa-green data-[state=active]:text-white">{t("tabGr")}</TabsTrigger>
                <TabsTrigger data-testid={GMOAA.grTabNotices} value="notices" className="data-[state=active]:bg-gmoaa-green data-[state=active]:text-white">{t("tabNotices")}</TabsTrigger>
              </TabsList>
              <TabsContent value="gr" className="mt-4 space-y-3">{grs.map((g) => <GRListItem key={g.id} gr={g} />)}</TabsContent>
              <TabsContent value="notices" className="mt-4 space-y-3">{notices.map((g) => <GRListItem key={g.id} gr={g} />)}</TabsContent>
            </Tabs>
          </div>
          <div className="space-y-6">
            <div>
              <h3 className="mb-3 font-serif text-xl font-semibold text-gmoaa-green">{t("upcoming")}</h3>
              <div className="space-y-3">
                {upcoming.map((u) => (
                  <div key={u.id} className="flex gap-3 rounded-xl border border-gmoaa-border-subtle bg-white p-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gmoaa-saffron-light text-gmoaa-saffron-hover"><CalendarDays size={18} /></span>
                    <div>
                      <p className="text-sm font-medium text-gmoaa-text-dark">{tf(u, "title")}</p>
                      <p className="text-xs text-gmoaa-text-muted">{formatDate(u.date)} · {tf(u, "place")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {ads.sidebar && <AdSlot ad={ads.sidebar} height="h-40" />}
          </div>
        </div>
      </section>
    ),

    bearers: (
      <section key="bearers" className="bg-gmoaa-surface py-14">
        <div className="gmoaa-container">
          <SectionHeader titleKey="bearersTitle" subtitleKey="bearersSubtitle" action={
            <Link to="/office-bearers" className="inline-flex items-center gap-1 text-sm font-medium text-gmoaa-green hover:text-gmoaa-saffron-hover">{t("viewAllBearers")} <ArrowRight size={15} /></Link>
          } />
          <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">{bearers.map((b) => <OfficeBearerCard key={b.id} bearer={b} />)}</div>
        </div>
      </section>
    ),

    districts: (
      <section key="districts" className="gmoaa-container py-12">
        {ads.mid && <div className="pb-10"><AdSlot ad={ads.mid} height="h-28" /></div>}
        <SectionHeader titleKey="districtsTitle" subtitleKey="districtsSubtitle" action={
          <Link to="/districts" className="inline-flex items-center gap-1 text-sm font-medium text-gmoaa-green hover:text-gmoaa-saffron-hover">{t("viewAllDistricts")} <ArrowRight size={15} /></Link>
        } />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">{districts.slice(0, 3).map((d) => <DistrictCard key={d.id} district={d} />)}</div>
        {districts.length > 3 && (
          <Link to="/districts" className="mt-4 flex items-center justify-center rounded-xl border border-dashed border-gmoaa-border-strong bg-gmoaa-green-light/50 py-4 text-sm font-medium text-gmoaa-green hover:bg-gmoaa-green-light">
            +{districts.length - 3} {t("moreDistricts")} <ArrowRight size={15} className="ml-1" />
          </Link>
        )}
      </section>
    ),

    sponsors: (
      <section key="sponsors" className="border-t border-gmoaa-border-subtle bg-gmoaa-surface py-12">
        <div className="gmoaa-container">
          <h3 className="text-center font-serif text-xl font-semibold text-gmoaa-green">{t("sponsorsTitle")}</h3>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            {sponsors.map((s) => (
              <div key={s.id} className="flex h-16 w-40 items-center justify-center rounded-lg border border-gmoaa-border-subtle bg-white px-3 text-center text-sm font-medium text-gmoaa-text-muted">{tf(s, "name")}</div>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-2xl bg-gmoaa-green px-6 py-6 text-white sm:flex-row">
            <div>
              <p className="font-serif text-xl font-semibold">{t("advertiseCta")}</p>
              <p className="text-sm text-white/80">{t("advertiseCtaSub")}</p>
            </div>
            <Button onClick={() => navigate("/advertise")} className="bg-gmoaa-gold text-gmoaa-green hover:bg-gmoaa-gold/90">{t("advertiseCta")} <ArrowRight size={16} className="ml-1" /></Button>
          </div>
        </div>
      </section>
    ),
  };

  return (
    <PageShell>
      {sections.map((s) => (visible(s.key) ? blocks[s.key] : null))}
    </PageShell>
  );
};

export default Home;
